import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Modal,
  Select,
  Typography,
  Row,
  Col,
  Table,
  Space,
  Tag,
} from "antd";
import { ProjectProps, TagProps } from "../../interfaces/interfaces";
import { ColumnsType } from "antd/es/table";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import DataContext from "../../context/DataContext";
import AuthContext from "../../context/AuthContext";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { filterOption, filterOptionGroup } from "../../utils/func/utils";
dayjs.extend(customParseFormat);
dayjs.extend(utc);

interface ProjectImportProps {
  open: boolean;
  previewData: any[];
  uploadedData: ProjectProps[];
  onClose: () => void;
  idClient?: string;
  handleConfirmUpload?: (columnMapping: any, next: () => void) => void;
  handleConfirmCreate?: (values: ProjectProps[]) => void;
}

const ProjectImport: React.FC<ProjectImportProps> = ({
  open,
  previewData,
  uploadedData,
  onClose,
  handleConfirmUpload,
  handleConfirmCreate,
}) => {
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>(
    {},
  );
  const { customerByClient, usersList } = useContext(DataContext);
  const { clientInformation } = useContext(AuthContext);
  const [isShowingPreview, setIsShowingPreview] = useState(true);
  const [values, setValues] = useState<ProjectProps[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 4 });

  useEffect(() => {
    if (uploadedData.length > 0) {
      setValues(uploadedData);
    }
  }, [uploadedData]);

  const projectFieldOptions = [
    { label: "Template", value: "projectTemplate" },
    { label: "Project Name", value: "name", required: true },
    { label: "Customer", value: "customer", required: true },
    { label: "Description", value: "description" },
    { label: "Start Date", value: "startDate" },
    { label: "End Date", value: "endDate" },
    { label: "Assigned To", value: "assignedTo", required: true },
    { label: "Status", value: "status" },
    { label: "Location", value: "location" },
    { label: "Source", value: "source" },
    { label: "Tags", value: "tags" },
  ];

  const columns: ColumnsType<ProjectProps> = [
    {
      title: "Template",
      dataIndex: "projectTemplate",
      key: "projectTemplate",
      render: (template) => template?.name || "-",
    },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Customers",
      dataIndex: "customers",
      key: "customers",
      render: (_, record, index) => (
        <Select
          filterOption={filterOption}
          showSearch
          style={{ width: 180 }}
          value={(record.customers?.[0] as any)?._id}
          onChange={(val) => {
            const absoluteIndex =
              (pagination.current - 1) * pagination.pageSize + index;
            const selected = customerByClient.find((c) => c._id === val);
            if (selected) {
              const updated = [...values];
              updated[absoluteIndex].customers = [selected];
              setValues(updated);
            }
          }}
          options={customerByClient.map((customer) => ({
            label: customer.DisplayName,
            value: customer._id,
          }))}
          placeholder="Select Customer"
        ></Select>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <div
          style={{
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => (date ? dayjs.utc(date).format("MM/DD/YYYY") : "-"),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (date) => (date ? dayjs.utc(date).format("MM/DD/YYYY") : "-"),
    },
    {
      title: "Assigned To",
      dataIndex: "assignedTo",
      key: "assignedTo",
      render: (_, record, index) => (
        <Select
          filterOption={filterOption}
          showSearch
          style={{ width: 160 }}
          value={(record.assignedTo as any)?._id}
          onChange={(val) => {
            const absoluteIndex =
              (pagination.current - 1) * pagination.pageSize + index;
            const selected = usersList.find((u) => u._id === val);
            if (selected) {
              const updated = [...values];
              updated[absoluteIndex].assignedTo = selected;
              setValues(updated);
            }
          }}
          options={usersList.map((user) => ({
            label: user.name,
            value: user._id,
          }))}
          placeholder="Select User"
        ></Select>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record, index) => (
        <Select
          filterOption={filterOptionGroup}
          showSearch
          style={{ width: 160 }}
          value={record.status?._id}
          onChange={(val) => {
            const absoluteIndex =
              (pagination.current - 1) * pagination.pageSize + index;
            const selected = clientInformation?.projectStatus?.find(
              (s) => s._id === val,
            );
            if (selected) {
              const updated = [...values];
              updated[absoluteIndex].status = selected;
              setValues(updated);
            }
          }}
          placeholder="Select Status"
        >
          <Select.OptGroup label="Template Status">
            {record.projectTemplate?.projectStatus.map((s) => (
              <Select.Option key={s._id} value={s._id}>
                {s.name}
              </Select.Option>
            ))}
          </Select.OptGroup>
          <Select.OptGroup label="General Status">
            {clientInformation?.projectStatus.map((s) => (
              <Select.Option key={s._id as any} value={s._id}>
                {s.name}
              </Select.Option>
            ))}
          </Select.OptGroup>
        </Select>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (_, record, index) => (
        <Select
          filterOption={filterOption}
          showSearch
          style={{ width: 160 }}
          value={record.location?._id}
          onChange={(val) => {
            const absoluteIndex =
              (pagination.current - 1) * pagination.pageSize + index;
            const selected = clientInformation?.locations?.find(
              (l) => l._id === val,
            );
            if (selected) {
              const updated = [...values];
              updated[absoluteIndex].location = selected;
              setValues(updated);
            }
          }}
          options={clientInformation?.locations?.map((location) => ({
            label: location.name,
            value: location._id,
          }))}
          placeholder="Select Location"
        ></Select>
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (_, record, index) => (
        <Select
          filterOption={filterOption}
          showSearch
          style={{ width: 160 }}
          value={record.source?._id}
          onChange={(val) => {
            const absoluteIndex =
              (pagination.current - 1) * pagination.pageSize + index;
            const selected = clientInformation?.sources?.find(
              (l) => l._id === val,
            );
            if (selected) {
              const updated = [...values];
              updated[absoluteIndex].source = selected;
              setValues(updated);
            }
          }}
          options={clientInformation?.sources?.map((source) => ({
            label: source.name,
            value: source._id,
          }))}
          placeholder="Select Source"
        ></Select>
      ),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (tags: TagProps[]) => (
        <div>
          {tags.map((tag: TagProps) => (
            <Tag
              key={tag._id}
              color={tag?.color}
              style={{
                maxWidth: "130px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tag.name}
            </Tag>
          ))}
        </div>
      ),
      responsive: ["xl", "lg", "md"],
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record, index) => (
        <Button
          type="link"
          onClick={() => {
            const absoluteIndex =
              (pagination.current - 1) * pagination.pageSize + index;
            const updated = [...values];
            updated.splice(absoluteIndex, 1);
            setValues(updated);
          }}
          icon={<DeleteOutlined />}
          style={{ color: "red" }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const handleClose = () => {
    setColumnMapping({});
    setValues([]);
    setIsShowingPreview(true);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      centered
      title={<>Preview and Upload Projects</>}
      width={previewData.length > 0 ? "100vw" : 500}
      footer={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          {isShowingPreview && (
            <Button
              type="primary"
              disabled={projectFieldOptions
                .filter((option) => option.required)
                .some(
                  (requiredOption) =>
                    !Object.values(columnMapping).includes(
                      requiredOption.value,
                    ),
                )}
              onClick={() => {
                handleConfirmUpload?.(columnMapping, () =>
                  setIsShowingPreview(false),
                );
              }}
            >
              {values.length === 0 ? "Confirm Upload" : "Confirm Edit"}
            </Button>
          )}

          {previewData.length > 0 && uploadedData.length > 0 && (
            <Button
              type="primary"
              icon={
                !isShowingPreview ? (
                  <ArrowLeftOutlined />
                ) : (
                  <ArrowRightOutlined />
                )
              }
              onClick={() => setIsShowingPreview((prev) => !prev)}
            >
              {!isShowingPreview ? "Back to the Preview" : "Edit Projects"}
            </Button>
          )}

          {!isShowingPreview && values.length > 0 && (
            <Button
              type="primary"
              onClick={() => {
                handleConfirmCreate?.(values);
                handleClose();
              }}
            >
              Create Projects
            </Button>
          )}
        </Space>
      }
    >
      {previewData.length > 0 ? (
        <Row gutter={24} style={{ marginTop: 20 }}>
          {!isShowingPreview ? (
            <Col span={24}>
              <Typography.Title level={5}>Edit Projects Info</Typography.Title>
              <Table
                size="small"
                dataSource={values}
                columns={columns}
                rowKey={(record) => record._id}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  showSizeChanger: false,
                  onChange: (page, pageSize) =>
                    setPagination({ current: page, pageSize }),
                }}
                scroll={{ x: true }}
              />
            </Col>
          ) : (
            <>
              <Col span={8}>
                <Typography.Title level={5}>
                  Map Project Fields to Excel Columns
                </Typography.Title>
                <Row gutter={[12, 12]}>
                  {projectFieldOptions.map(({ label, value, required }) => (
                    <React.Fragment key={value}>
                      <Col span={10}>
                        <strong>
                          {label}
                          {required && " *"}
                        </strong>
                      </Col>
                      <Col span={14}>
                        <Select
                          filterOption={filterOption}
                          showSearch
                          style={{ marginLeft: 12, width: 250 }}
                          placeholder="Select Excel Column"
                          options={Object.keys(previewData[0]).map(
                            (header) => ({
                              label: header,
                              value: header,
                            }),
                          )}
                          value={
                            Object.entries(columnMapping).find(
                              ([_, v]) => v === value,
                            )?.[0]
                          }
                          onChange={(selectedHeader) =>
                            setColumnMapping((prev) => {
                              const newMapping = Object.fromEntries(
                                Object.entries(prev).filter(
                                  ([_, v]) => v !== value,
                                ),
                              );
                              return { ...newMapping, [selectedHeader]: value };
                            })
                          }
                          allowClear
                        />
                      </Col>
                    </React.Fragment>
                  ))}
                </Row>
              </Col>
              <Col span={16}>
                <Typography.Title level={5}>Preview</Typography.Title>
                <Table
                  dataSource={previewData}
                  columns={Object.keys(previewData[0]).map((key) => ({
                    title: key,
                    dataIndex: key,
                    key,
                    render: (text) => {
                      let formatted = text;

                      if (text instanceof Date) {
                        formatted = dayjs(text).format("MM/DD/YYYY");
                      } else if (dayjs(text, "MM/DD/YY", true).isValid()) {
                        formatted = dayjs(text, "MM/DD/YY").format(
                          "MM/DD/YYYY",
                        );
                      }

                      return (
                        <div
                          style={{
                            maxWidth: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatted}
                        </div>
                      );
                    },
                  }))}
                  size="small"
                  bordered
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  scroll={{ x: true }}
                />
              </Col>
            </>
          )}
        </Row>
      ) : (
        <>
          All Projects uploaded Already exists in the database, try renaming and
          upload them again
        </>
      )}
    </Modal>
  );
};

export default ProjectImport;
